import React from 'react';
import { formatScheduleLabel, lessonFormForHeadcount } from '@/lib/utils';
import {
  minutes, clockLabel, fmtRange, layoutEventLanes, weekdayAbbr, studentDisplayName,
  subjectWithoutWhen, compactLessonLabel,
} from './calendarUtils';

/** Beige Raum-Kopfzeile unter dem Standortband. */
const ROOM_HEADER_BG = 'bg-[#EDE6D9]';

function studentList(e) {
  if (Array.isArray(e?.students) && e.students.length) return e.students;
  if (e?.student_label) return [{ id: 'label', name: e.student_label }];
  return [];
}

function EventBlock({
  e, top, h, onEventClick, onDragStart,
  lane = 0, laneCount = 1, focused = false,
}) {
  const privacy = e.privacy || (e.students?.length || e.student_label ? 'students' : 'course');
  const listed = privacy === 'busy' || privacy === 'course' ? [] : studentList(e);
  const students = listed;
  const showTeacher = privacy !== 'busy' && !!e.teacher_name;
  const eventTitle = privacy === 'busy' ? 'Belegt' : (formatScheduleLabel(e.name) || e.name || '');
  const subject = compactLessonLabel(lessonFormForHeadcount(subjectWithoutWhen(eventTitle), listed.length));
  const bodyHeight = Math.max(
    0,
    h - EVENT_CHROME_PX - EVENT_LINE_PX - (showTeacher ? EVENT_LINE_PX : 0) - (subject ? EVENT_LINE_PX : 0),
  );
  const when = [weekdayAbbr(e.date), fmtRange(e.start, e.end)].filter(Boolean).join(' ');
  const reservation = e.kind === 'reservation' && privacy !== 'busy';
  const toneName = e.cancelled ? 'cancelled' : reservation ? 'reservation' : 'lesson';
  const tone = e.cancelled
    ? 'bg-slate-100 text-slate-400 line-through'
    : reservation
      ? 'bg-amber-100 text-amber-950 border border-amber-300'
      : 'bg-[#F3F0E8] text-slate-900 border border-slate-300';
  const widthPct = 100 / Math.max(1, laneCount);

  return (
    <div
      role="button"
      tabIndex={0}
      draggable={Boolean(onDragStart && e.course_id)}
      className={`absolute z-0 flex flex-col overflow-hidden text-left rounded-[2px] px-1 py-0 text-[10px] leading-[11px] cursor-pointer ${tone}`}
      style={{
        top,
        height: h,
        left: `calc(${widthPct * lane}% + 2px)`,
        width: `calc(${widthPct}% - 4px)`,
        boxShadow: focused ? 'inset 0 0 0 2px #1e293b' : undefined,
      }}
      data-focus={focused ? 'true' : undefined}
      data-calendar-tone={toneName}
      data-privacy={privacy}
      data-lane={lane}
      data-calendar-event=""
      data-testid={`calendar-block-${e.kind === 'reservation' ? `resv-${e.reservation_id}` : (e.course_id || e.room_id || 'busy')}-${e.date || ''}`}
      onDragStart={(ev) => onDragStart?.(ev, e)}
      onClick={(ev) => { ev.stopPropagation(); onEventClick?.(e); }}
      onKeyDown={(ev) => {
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault();
          ev.stopPropagation();
          onEventClick?.(e);
        }
      }}
    >
      <div className="shrink-0 truncate whitespace-nowrap font-semibold" style={{ height: EVENT_LINE_PX, lineHeight: `${EVENT_LINE_PX}px` }}>{when}</div>
      <div
        className="min-h-0 overflow-x-hidden overflow-y-auto"
        style={{ height: bodyHeight }}
        data-testid="calendar-block-body"
        onWheel={(ev) => {
          const el = ev.currentTarget;
          if (el.scrollHeight > el.clientHeight + 1) ev.stopPropagation();
        }}
      >
        {students.length > 0 && (
          <div data-testid="calendar-block-students">
            {students.map((s) => (
              <div key={s.id || s.name} data-testid="calendar-block-student" className="whitespace-nowrap" title={studentDisplayName(s.name)}>
                {studentDisplayName(s.name)}
              </div>
            ))}
          </div>
        )}
      </div>
      {showTeacher && (
        <div
          className="shrink-0 truncate whitespace-nowrap font-bold"
          style={{ height: EVENT_LINE_PX, lineHeight: `${EVENT_LINE_PX}px` }}
          title={e.teacher_name}
          data-testid="calendar-block-teacher"
        >{e.teacher_name}</div>
      )}
      {subject && (
        <div
          className="shrink-0 truncate whitespace-nowrap opacity-80"
          style={{ height: EVENT_LINE_PX, lineHeight: `${EVENT_LINE_PX}px` }}
          title={subject}
          data-testid="calendar-block-subject"
        >{subject}</div>
      )}
    </div>
  );
}

/** Gestapelte sticky-tops: Tag (h-7) → Standort (h-7) → Raum (h-8). */
export function headerStickTops(groups) {
  if (!groups) return { location: null, room: 'top-0', nested: false };
  const nested = groups.some((g) => g.subGroups);
  if (nested) return { location: 'top-7', room: 'top-14', nested: true };
  return { location: 'top-0', room: 'top-7', nested: false };
}

function LocationBand({ label, testId, stickTop }) {
  return (
    <div
      className={`h-7 border-b border-brand-anthracite/80 bg-brand-anthracite text-white overflow-hidden relative sticky z-20 ${stickTop || 'top-0'}`}
      data-testid={testId}
      title={label}
    >
      {/* sticky left: Standortname bleibt beim horizontalen Scrollen im sichtbaren Bereich */}
      <div className="sticky left-14 z-[5] inline-flex h-full max-w-full items-center px-2 text-[11px] font-bold tracking-wide truncate">
        {label}
      </div>
    </div>
  );
}

function DayBand({ label, testId }) {
  return (
    <div
      className="h-7 border-b border-slate-300 bg-slate-200 px-1 flex items-center justify-center text-xs font-bold text-slate-800 truncate sticky top-0 z-30"
      title={label}
      data-testid={testId}
    >
      {label}
    </div>
  );
}

/** 10px Schrift, 11px Zeile, 2px Rahmen. Vier Zeilen passen in einen 30-Minuten-Block. */
export const EVENT_LINE_PX = 11;
export const EVENT_CHROME_PX = 2;

export function eventBlockFitsLines(heightPx, lines = 3) {
  return heightPx + 0.01 >= EVENT_CHROME_PX + EVENT_LINE_PX * lines;
}

/**
 * Untere Breite einer Raumspalte. Fünfzehn Spalten plus die Uhrzeitskala (56px)
 * bleiben in einem 1920px-Fenster neben der 250px-Seitenleiste. Ist das Fenster
 * schmaler, scrollt die Übersicht seitlich; Räume werden nicht ausgeblendet.
 * Überlappende Termine teilen sich die Spalte, statt sie zu verbreitern.
 */
const COLUMN_MIN_PX = 104;

export function columnMinWidth() {
  return COLUMN_MIN_PX;
}

function ColumnBody({
  col, dayStart, dayEnd, raster, pxPerMin, height, ticks, todayIso, onEventClick,
  onSlotClick,
  onEventDrop, roomStickTop = 'top-0', focusCourseId = null,
}) {
  const isToday = col.date && col.date === todayIso;
  const widthPx = columnMinWidth(col.entries);
  return (
    <div
      className="shrink-0 border-r border-slate-200"
      style={{ flex: `1 0 ${widthPx}px`, width: widthPx, minWidth: widthPx }}
    >
      <div className={`h-8 border-b px-1 flex items-center justify-center text-[11px] font-semibold truncate sticky z-30 ${roomStickTop} ${
        isToday ? 'bg-white text-slate-900 border-b-2 border-brand-anthracite' : `border-slate-300 ${ROOM_HEADER_BG} text-slate-800`
      }`} style={{ backgroundColor: isToday ? '#ffffff' : '#EDE6D9' }} title={isToday ? `Heute · ${col.label}` : col.label} data-today={isToday ? 'true' : undefined} data-testid={`calendar-room-header-${col.id}`}>
        {col.label}
      </div>
      <div className="relative overflow-hidden bg-white" style={{ height }}
           data-testid={`calendar-col-${col.id}`}
           onDragOver={(e) => {
             if (!onEventDrop) return;
             e.preventDefault();
             e.dataTransfer.dropEffect = 'move';
           }}
           onDrop={(e) => {
             if (!onEventDrop) return;
             e.preventDefault();
             e.stopPropagation();
             let event;
             try {
               event = JSON.parse(e.dataTransfer.getData('application/json'));
             } catch {
               return;
             }
             const rect = e.currentTarget.getBoundingClientRect();
             const y = Math.max(0, Math.min(height, e.clientY - rect.top));
             const targetMinutes = Math.max(
               dayStart,
               Math.min(dayEnd - raster, dayStart + Math.round(y / pxPerMin / raster) * raster),
             );
             const oldDuration = Math.max(raster, minutes(event.end) - minutes(event.start));
             const hhmm = (value) => `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`;
             onEventDrop(event, {
               date: col.date || event.date,
               start: hhmm(targetMinutes),
               end: hhmm(targetMinutes + oldDuration),
               room_id: col.room_id !== undefined ? col.room_id : event.room_id,
               room_name: col.room_name || col.label || event.room_name,
               location_name: col.location_name || event.location_name,
               teacher_id: col.teacher_id || event.teacher_id,
               teacher_name: col.teacher_name || event.teacher_name,
             });
           }}
           onClick={(e) => {
             if (!onSlotClick) return;
             const rect = e.currentTarget.getBoundingClientRect();
             const y = e.clientY - rect.top;
             const mins = dayStart + Math.round(y / pxPerMin / raster) * raster;
             onSlotClick({ ...col, minutes: mins });
           }}>
        {ticks.map((m) => (
          <div key={m} className={`absolute left-0 right-0 ${m % 60 === 0 ? 'border-t border-slate-200' : 'border-t border-slate-100'}`}
               style={{ top: (m - dayStart) * pxPerMin }} />
        ))}
        {(() => {
          const lanes = layoutEventLanes(col.entries || []);
          return (col.entries || []).map((e, i) => {
            const top = (minutes(e.start) - dayStart) * pxPerMin;
            const h = Math.max(22, (minutes(e.end) - minutes(e.start)) * pxPerMin - 2);
            return (
              <EventBlock
                key={`${e.reservation_id || e.course_id}-${e.date}-${i}`}
                e={e}
                lane={lanes.laneOf.get(i) || 0}
                laneCount={lanes.count}
                top={top}
                h={h}
                onEventClick={onEventClick}
                onDragStart={(ev, event) => {
                  ev.dataTransfer.effectAllowed = 'move';
                  ev.dataTransfer.setData('application/json', JSON.stringify(event));
                }}
                focused={Boolean(focusCourseId) && e.course_id === focusCourseId}
              />
            );
          });
        })()}
      </div>
    </div>
  );
}

/** Spalten zu Standort-/Tagesgruppen (optional verschachtelt) zusammenfassen. */
export function buildColumnGroups(columns) {
  if (!columns.some((c) => c.group)) return null;
  const groups = [];
  for (const col of columns) {
    const key = col.groupKey || col.group || '_';
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.cols.push(col);
    else {
      groups.push({
        key,
        label: col.group || '',
        kind: col.groupKind || 'location',
        cols: [col],
      });
    }
  }
  return groups.map((g) => {
    const hasSub = g.cols.some((c) => c.subGroup);
    if (!hasSub) return { ...g, subGroups: null };
    const subGroups = [];
    for (const col of g.cols) {
      const sk = col.subGroupKey || col.subGroup || '_';
      const last = subGroups[subGroups.length - 1];
      if (last && last.key === sk) last.cols.push(col);
      else {
        subGroups.push({
          key: sk,
          label: col.subGroup || '',
          kind: col.subGroupKind || 'location',
          cols: [col],
        });
      }
    }
    return { ...g, subGroups };
  });
}

function headerSpacerClass(groups) {
  if (!groups) return 'h-8';
  const nested = groups.some((g) => g.subGroups);
  // Tag + Standort + Raum  /  Standort + Raum
  return nested ? 'h-[5.5rem]' : 'h-[3.75rem]';
}

export default function TimeGrid({
  columns,
  dayStart,
  dayEnd,
  raster = 10,
  todayIso,
  onEventClick,
  onSlotClick,
  onEventDrop,
  focusCourseId = null,
}) {
  // Gröberes Raster (30 statt 5) streckt die Minute, damit der Block höher wird.
  const pxPerMin = 1.6 * ((raster || 10) / 10);
  const height = Math.max(120, (dayEnd - dayStart) * pxPerMin);
  const ticks = [];
  for (let m = dayStart; m <= dayEnd; m += raster) ticks.push(m);

  const groups = buildColumnGroups(columns);
  const stick = headerStickTops(groups);
  const bodyProps = {
    dayStart, dayEnd, raster, pxPerMin, height, ticks, todayIso,
    onEventClick, onSlotClick, onEventDrop, focusCourseId,
  };
  const colProps = { ...bodyProps, roomStickTop: stick.room };

  const renderLocationBlock = (loc, keyPrefix) => (
    <div
      key={`${keyPrefix}-${loc.key}`}
      className="flex flex-col border-r-2 border-brand-anthracite/40"
      style={{ flex: '1 0 auto' }}
      data-testid={`calendar-location-group-${loc.key}`}
    >
      <LocationBand
        label={loc.label}
        testId={`calendar-group-${loc.key}`}
        stickTop={stick.location}
      />
      <div className="flex flex-1">
        {loc.cols.map((col) => (
          <ColumnBody key={col.id} col={col} {...colProps} />
        ))}
      </div>
    </div>
  );

  return (
    <div className="overflow-auto flex-1 bg-white" data-testid="calendar-time-grid">
      <div className="flex w-max min-w-full">
        <div className="w-14 shrink-0 border-r border-slate-200 bg-white sticky left-0 z-40">
          <div className={`${headerSpacerClass(groups)} sticky top-0 z-30 bg-white border-b border-slate-200`} />
          <div className="relative" style={{ height }}>
            {ticks.filter((m) => m % 30 === 0).map((m) => (
              <div key={m} className="absolute right-1 text-[10px] text-slate-500 -translate-y-1/2"
                   style={{ top: (m - dayStart) * pxPerMin }}>{clockLabel(m)}</div>
            ))}
          </div>
        </div>
        {groups ? groups.map((g) => (
          g.subGroups ? (
            <div
              key={g.key}
              className="flex flex-col border-r-2 border-slate-400"
              style={{ flex: '1 0 auto' }}
              data-testid={`calendar-day-group-${g.key}`}
            >
              <DayBand label={g.label} testId={`calendar-group-${g.key}`} />
              <div className="flex flex-1">
                {g.subGroups.map((sg) => renderLocationBlock(sg, g.key))}
              </div>
            </div>
          ) : (
            renderLocationBlock(g, 'top')
          )
        )) : columns.map((col) => (
          <ColumnBody key={col.id} col={col} {...colProps} />
        ))}
      </div>
    </div>
  );
}
